'use client'

import { useState } from 'react'

import { Loader2 } from 'lucide-react'

import type { Mission } from '@/features/missions/types/mission'
import { useOptimizedRecords } from '@/shared/hooks/useOptimizedQuery'
import { cn } from '@/shared/lib/utils'
import { api } from '@/shared/services/api'
import { selectClassName } from '@/shared/ui/input-styles'

import { SchoolAsyncSelect } from './SchoolAsyncSelect'

interface SchoolYearOption {
  value: number
  label: string
}

interface SchoolDataFormProps {
  mission: Mission
  onSuccess?: () => void
  onError?: (errors: Record<string, string[]>) => void
}

const labelClassName = 'block text-sm font-medium text-gray-700 mb-1'

export function SchoolDataForm({
  mission: _mission,
  onSuccess,
  onError
}: SchoolDataFormProps) {
  const [schoolId, setSchoolId] = useState<number | ''>('')
  const [schoolYear, setSchoolYear] = useState<number | ''>('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({})

  const { records: schoolYears, isLoading: loadingSchoolYears } =
    useOptimizedRecords<SchoolYearOption>({ path: '/user/school_years' })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isSubmitting) return
    const sid = schoolId === '' ? undefined : Number(schoolId)
    const sy = schoolYear === '' ? undefined : Number(schoolYear)
    if (sid === undefined || sy === undefined) {
      setFieldErrors({
        school_id: sid === undefined ? ['Escola é obrigatória'] : [],
        school_year: sy === undefined ? ['Série é obrigatória'] : []
      })
      return
    }
    setFieldErrors({})
    setIsSubmitting(true)
    try {
      await api.patch('/user/school_data', {
        record: { school_id: sid, school_year: sy }
      })
      onSuccess?.()
    } catch (err: unknown) {
      const data =
        err && typeof err === 'object' && 'response' in err
          ? (
              err as {
                response?: { data?: { errors?: Record<string, string[]> } }
              }
            ).response?.data
          : undefined
      const errors = data?.errors ?? {
        _base: ['Não foi possível atualizar. Tente novamente.']
      }
      setFieldErrors(errors)
      onError?.(errors)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div>
        <label htmlFor="school-data-school" className={labelClassName}>
          Escola
        </label>
        <SchoolAsyncSelect
          id="school-data-school"
          value={schoolId}
          onChange={setSchoolId}
          hasError={!!fieldErrors.school_id?.length}
        />
        {fieldErrors.school_id?.length ? (
          <p className="mt-1 text-sm text-red-600">
            {fieldErrors.school_id[0]}
          </p>
        ) : null}
      </div>

      <div>
        <label htmlFor="school-data-year" className={labelClassName}>
          Série
        </label>
        <select
          id="school-data-year"
          value={schoolYear === '' ? '' : String(schoolYear)}
          onChange={e =>
            setSchoolYear(e.target.value ? Number(e.target.value) : '')
          }
          className={cn(
            selectClassName,
            fieldErrors.school_year && 'border-red-500'
          )}
          disabled={loadingSchoolYears}
          required
        >
          <option value="">Selecione a série</option>
          {schoolYears.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {fieldErrors.school_year?.length ? (
          <p className="mt-1 text-sm text-red-600">
            {fieldErrors.school_year[0]}
          </p>
        ) : null}
      </div>

      {fieldErrors._base?.length ? (
        <p className="text-sm text-red-600">{fieldErrors._base[0]}</p>
      ) : null}

      <button
        type="submit"
        disabled={isSubmitting || loadingSchoolYears}
        className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 size-4 animate-spin" />
            Atualizando...
          </>
        ) : (
          'Atualizar dados escolares'
        )}
      </button>
    </form>
  )
}
